import apolloClient from "@/apollo/apollo";
import gql from "graphql-tag";

const state = {
    packages: [],
    patientPackages: [],
};

const getters = {
    getAllPackages: (state) => state.packages,
    getPatientPackages: (state) => state.patientPackages,
};

const mutations = {
    setAllPackages(state, packages) {
        state.packages = packages.slice();
    },
    setPatientPackages(state, packages) {
        state.patientPackages = packages.slice();
    },
};

const actions = {
    async fetchAllPackages({ commit }, status) {
        try {
            const response = await apolloClient.query({
                query: gql`
          query findAllPackages($status: PackageStatusEnum) {
            findAllPackages(status: $status) {
              id
              name
              totalBooking
              price
              noOfDay
              note
              status
              creationDate
            }
          }
        `,
                variables: { status },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            commit("setAllPackages", response.data.findAllPackages);
            return response.data.findAllPackages;
        } catch (error) {
            console.error("Failed to fetch packages:", error);
            return [];
        }
    },

    async createPackage({ dispatch }, packageInput) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation createPackage($packageInput: PackageInput!) {
            createPackage(packageInput: $packageInput) {
              id
              name
            }
          }
        `,
                variables: { packageInput },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.createPackage;
        } catch (error) {
            console.error("Failed to create package:", error);
            return null;
        }
    },

    async updatePackage({ dispatch }, { id, packageInput }) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation updatePackage($id: Int!, $packageInput: PackageInput!) {
            updatePackage(id: $id, packageInput: $packageInput) {
              id
              name
              color
            }
          }
        `,
                variables: { id: +id, packageInput },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.updatePackage;
        } catch (error) {
            console.error("Failed to update package:", error);
            return null;
        }
    },

    async fetchPatientPackages({ commit }, patientId) {
        try {
            const response = await apolloClient.query({
                query: gql`
          query findAllPatientPackages($patientId: Int!) {
            findAllPatientPackages(patientId: $patientId) {
              id
              packageId
              package {
                id
                name
              }
              totalBooking
              totalBookingUsed
              price
              noOfDay
              startAt
              endAt
              note
              status
              creationDate
              totalPaid
              remainingDays
              isLegacy
              userId
              user {
                id
                contact {
                  fullName
                }
              }
              practitioners {
                id
                contact {
                  fullName
                }
              }
              actualUsageBreakdown {
                departmentId
                departmentName
                totalUsed
                practitioners {
                  practitionerId
                  practitionerName
                  usedCount
                }
              }
              departmentAllocations {
                id
                departmentId
                department {
                  id
                  name
                  listOfAppointmentTypeService {
                    id
                    name
                    price
                  }
                }
                allocatedBooking
                usedBooking
                remainingBooking
                startTime
                endTime
                daysOfWeek
                serviceTypeAllocations {
                  id
                  departmentId
                  appointmentTypeServiceId
                  appointmentTypeService {
                    id
                    name
                    price
                  }
                  allocatedBooking
                  usedBooking
                  remainingBooking
                  bookedCount
                  arrivedCount
                  notArrivedCount
                  canceledCount
                }
                bookings {
                  id
                  bookingDate
                  startTime
                  endTime
                  status
                  appointmentTypeService {
                    name
                  }
                  user {
                    contact {
                      fullName
                    }
                  }
                  attendees {
                    patientId
                    patientArriveStatus
                  }
                }
              }
            }
          }
        `,
                variables: { patientId: +patientId },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
                fetchPolicy: "network-only",
            });
            const sortedPackages = [...response.data.findAllPatientPackages].sort((a, b) => {
                const dateA = new Date(a.creationDate || 0).getTime();
                const dateB = new Date(b.creationDate || 0).getTime();
                return dateB - dateA;
            });
            commit("setPatientPackages", sortedPackages);
            return sortedPackages;
        } catch (error) {
            console.error("Failed to fetch patient packages:", error);
            return [];
        }
    },

    async assignPackageToPatient({ dispatch }, { patientId, input }) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation assignPackageToPatient($patientId: Int!, $input: PatientPackageInput!) {
            assignPackageToPatient(patientId: $patientId, input: $input) {
              id
            }
          }
        `,
                variables: { patientId: +patientId, input },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.assignPackageToPatient;
        } catch (error) {
            console.error("Failed to assign package:", error);
            return null;
        }
    },

    async updatePatientPackage({ dispatch }, { id, input }) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation updatePatientPackage($id: Int!, $input: PatientPackageInput!) {
            updatePatientPackage(id: $id, input: $input) {
              id
            }
          }
        `,
                variables: { id: +id, input },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.updatePatientPackage;
        } catch (error) {
            console.error("Failed to update patient package:", error);
            return null;
        }
    },

    async updatePackagePractitioners({ dispatch }, { patientPackageId, practitionerIds }) {
        try {
            const response = await apolloClient.mutate({
                mutation: gql`
          mutation updatePackagePractitioners($patientPackageId: Int!, $practitionerIds: [Int]!) {
            updatePackagePractitioners(patientPackageId: $patientPackageId, practitionerIds: $practitionerIds) {
              id
              status
            }
          }
        `,
                variables: { patientPackageId: +patientPackageId, practitionerIds },
                context: {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("accessToken"),
                    },
                },
            });
            return response.data.updatePackagePractitioners;
        } catch (error) {
            console.error("Failed to update practitioners:", error);
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
